import { useState } from 'react';
import {
  AGENTS,
  AgentMark,
  CitationChip,
  ConfidenceMeter,
  ConflictPair,
  CoordinateRoleBadge,
  EvidenceCard,
  GapNotice,
  ModelProse,
  ProvenanceGutter,
  PublicationBanner,
  UncertaintyIndicator,
  VerificationRule,
  type AgentKey,
  type CoordinateRole,
  type Fact,
  type LegendaLang,
  type Verification,
} from '../components/legenda';
import { APP_VERSION_LABEL } from '../version';

/**
 * Living reference for the ЛЕГЕНДА design system.
 *
 * Deliberately outside the authenticated routes: the primitives are what the
 * whole product's credibility rests on, and they have to be reviewable — by a
 * designer, by a reviewer, on a machine with no database — without an account.
 * It renders no archive data, only the fixed sample values documented in the
 * design brief, so there is nothing here to leak.
 */

const RAMP: Verification[] = [
  'verified',
  'reviewed_supported',
  'high_confidence_candidate',
  'medium_confidence_candidate',
  'low_confidence_candidate',
  'conflict',
  'unknown',
  'rejected',
];

const ROLES: CoordinateRole[] = [
  'survey_certified',
  'candidate_transformation',
  'cross_document_orientation',
  'raw_local',
];

const RESERVES: Fact<number> = {
  raw: '1.172.990,48 т',
  normalized: 1_172_990.48,
  unit: 'т',
  verification: 'verified',
  sourceFileId: 'sf-1982-001',
  pageId: 'pg-1982-118',
  evidenceRegionId: 'ER-1982-04417',
  extractionRunId: 'r-2026-06-11',
  datasetVersionId: 'dv-1.4.0',
  citation: '1982 · с. 118',
};

const MONTMORILLONITE: Fact<number> = {
  raw: '68,4%',
  normalized: 68.4,
  unit: '%',
  verification: 'medium_confidence_candidate',
  sourceFileId: 'sf-1963-001',
  pageId: 'pg-1963-047',
  evidenceRegionId: 'ER-1963-00912',
  extractionRunId: 'r-2026-06-11',
  datasetVersionId: 'dv-1.4.0',
  citation: '1963 · с. 47',
};

const NO_INCLINOMETRY: Fact<number> = {
  raw: '—',
  normalized: null,
  unit: null,
  verification: 'unknown',
  sourceFileId: '—',
  pageId: '—',
  evidenceRegionId: '—',
  extractionRunId: 'r-2026-06-11',
  datasetVersionId: 'dv-1.4.0',
  citation: 'не найдено',
};

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section style={{ borderTop: '1px solid var(--lg-rule)', padding: 'var(--lg-sp-6) 0' }}>
      <header style={{ display: 'flex', gap: 'var(--lg-sp-3)', alignItems: 'baseline', marginBottom: 'var(--lg-sp-4)' }}>
        <span
          style={{
            fontFamily: 'var(--lg-font-mono)', fontSize: 'var(--lg-text-cap)', color: 'var(--lg-ink-3)',
            border: '1px solid var(--lg-rule)', borderRadius: 'var(--lg-radius-pill)', padding: '3px 10px',
          }}
        >
          {n}
        </span>
        <h2 style={{ margin: 0, fontSize: 'var(--lg-text-xl)', letterSpacing: '-0.02em' }}>{title}</h2>
      </header>
      {children}
    </section>
  );
}

function Panel({ surface, children }: { surface: 'light' | 'dark'; children: React.ReactNode }) {
  return (
    <div
      data-legenda-surface={surface}
      style={{
        padding: 'var(--lg-sp-4)', border: '1px solid var(--lg-rule)',
        borderRadius: 'var(--lg-radius-md)', boxShadow: 'var(--lg-shadow-2), var(--lg-edge-hi)',
      }}
    >
      {children}
    </div>
  );
}

export default function DesignSystem() {
  const [lang, setLang] = useState<LegendaLang>('ru');

  const gallery = (
    <>
      <div style={{ display: 'grid', gap: 6 }}>
        {RAMP.map((status) => (
          <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 'var(--lg-sp-3)' }}>
            <VerificationRule status={status} lang={lang} />
            <code style={{ color: 'var(--lg-ink-3)', fontSize: 'var(--lg-text-micro)' }}>{status}</code>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 'var(--lg-sp-4)' }}>
        <EvidenceCard parameter="Запасы, блоки 80–117, категория C2" fact={RESERVES} agent="ZAP" lang={lang} fractionDigits={2} />
        <EvidenceCard parameter="Содержание монтмориллонита" fact={MONTMORILLONITE} agent="MIN" lang={lang} fractionDigits={1} />
        <EvidenceCard parameter="Инклинометрия ствола" fact={NO_INCLINOMETRY} agent="SKV" lang={lang} />
      </div>

      <div style={{ marginTop: 'var(--lg-sp-4)' }}>
        <ModelProse lang={lang}>
          <p>
            Обе величины относятся к одним и тем же блокам и напечатаны в одном отчёте, поэтому
            расхождение <CitationChip value="126 337,48 т" source="Δ расчёт" verification="conflict" lang={lang} />{' '}
            нельзя объяснить разными периодами подсчёта. Величина из заключения{' '}
            <CitationChip value="1 172 990,48 т" source="с. 118" verification="verified" lang={lang} /> подтверждена
            рецензентом; величина из введения{' '}
            <CitationChip value="1 046 653,00 т" source="с. 6" verification="high_confidence_candidate" lang={lang} />{' '}
            остаётся кандидатом.
          </p>
        </ModelProse>
      </div>

      <div style={{ marginTop: 'var(--lg-sp-4)' }}>
        <ConflictPair
          title="Расхождение итогов запасов, блоки 80–117"
          lang={lang}
          a={{ location: 'Заключение / аннотация · с. 118', value: 1_172_990.48, unit: 'т', verification: 'verified', evidenceRegionId: 'ER-1982-04417' }}
          b={{ location: 'Введение · с. 6', value: 1_046_653.0, unit: 'т', verification: 'high_confidence_candidate', evidenceRegionId: 'ER-1982-00212' }}
          status={<span className="lg-chip lg-chip--conflict">не разрешён · критический</span>}
        />
      </div>

      <div style={{ marginTop: 'var(--lg-sp-4)' }}>
        <GapNotice
          title="Нет данных: каталог координат устьев скважин"
          lang={lang}
          explanation="Приложение 26 заявлено в оглавлении отчёта, но физически отсутствует в архиве. Без него привязка устьев к глобальной системе координат не может быть подтверждена съёмкой ни для одной скважины."
          receipt={{
            sourceGroups: 13,
            pages: 3544,
            evidenceRegions: 178_339,
            gapRecordId: 'GAP-1982-03',
            blocksPublication: true,
            resolvedBy: 'поступление приложения 26 либо полевая съёмка устьев',
          }}
        />
      </div>

      <div style={{ marginTop: 'var(--lg-sp-4)', display: 'grid', gap: 6 }}>
        {ROLES.map((role) => (
          <div key={role} style={{ display: 'flex', gap: 'var(--lg-sp-4)', alignItems: 'center', flexWrap: 'wrap' }}>
            <CoordinateRoleBadge role={role} lang={lang} />
            {role === 'candidate_transformation' ? <UncertaintyIndicator metres={78} lang={lang} /> : null}
            {role === 'survey_certified' ? (
              <span className="lg-chip lg-chip--human">0 из 779 в архиве</span>
            ) : null}
            {role === 'raw_local' ? <span className="lg-chip lg-chip--absent">493 · не наносится на WGS84</span> : null}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 'var(--lg-sp-4)', flexWrap: 'wrap', marginTop: 6 }}>
          {[30, 42, 78, 100].map((m) => (
            <UncertaintyIndicator key={m} metres={m} lang={lang} />
          ))}
        </div>
      </div>

      <div style={{ marginTop: 'var(--lg-sp-4)' }}>
        <ConfidenceMeter status="high_confidence_candidate" lang={lang} />
      </div>

      <div style={{ marginTop: 'var(--lg-sp-4)' }}>
        <PublicationBanner state="provisional_only" lang={lang} datasetVersion="v1.5.0-rc3">
          <div style={{ padding: 'var(--lg-sp-3)' }}>
            <ProvenanceGutter agent="ZAP" lang={lang} verification="low_confidence_candidate" citation="черновик">
              <div className="lg-ev__param">Прогнозные ресурсы, категория P1</div>
              <div className="lg-ev__value">
                2 410 000<span className="lg-ev__unit"> т</span>
              </div>
            </ProvenanceGutter>
          </div>
        </PublicationBanner>
      </div>

      <div style={{ marginTop: 'var(--lg-sp-4)', display: 'flex', gap: 'var(--lg-sp-4)', flexWrap: 'wrap' }}>
        {(Object.keys(AGENTS) as AgentKey[]).map((key) => (
          <AgentMark key={key} agent={key} withName />
        ))}
      </div>
    </>
  );

  return (
    <div style={{ background: 'var(--lg-ground)', color: 'var(--lg-ink)', minHeight: '100vh', fontFamily: 'var(--lg-font-sans)' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 var(--lg-sp-5) var(--lg-sp-8)' }}>
        <header style={{ padding: 'var(--lg-sp-7) 0 var(--lg-sp-4)' }}>
          <div style={{ fontSize: 'var(--lg-text-micro)', letterSpacing: '.13em', textTransform: 'uppercase', color: 'var(--lg-ink-3)', fontWeight: 650 }}>
            Design system · AI-Геолог · {APP_VERSION_LABEL}
          </div>
          <h1 style={{ margin: 'var(--lg-sp-3) 0', fontSize: 42, letterSpacing: '-0.03em', lineHeight: 1.05 }}>Легенда</h1>
          <p style={{ margin: 0, maxWidth: '64ch', color: 'var(--lg-ink-2)', fontSize: 'var(--lg-text-md)', lineHeight: 1.6 }}>
            Уверенность кодируется рисунком линии, а не цветом — так же, как на геологической карте
            наблюдённый контакт сплошной, предполагаемый штриховой, скрытый пунктирный. Цвет остаётся
            свободным для другого: кто говорит — архив, детерминированный расчёт, модель или рецензент.
          </p>
          <div style={{ display: 'flex', gap: 4, marginTop: 'var(--lg-sp-4)' }}>
            {(['ru', 'hy', 'en'] as LegendaLang[]).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setLang(code)}
                aria-pressed={lang === code}
                style={{
                  font: 'inherit', fontSize: 'var(--lg-text-sm)', cursor: 'pointer',
                  padding: '5px 13px', borderRadius: 'var(--lg-radius-pill)',
                  border: '1px solid var(--lg-rule-strong)',
                  background: lang === code ? 'var(--lg-ink)' : 'var(--lg-surface)',
                  color: lang === code ? 'var(--lg-ground)' : 'var(--lg-ink)',
                }}
              >
                {code === 'ru' ? 'Рус' : code === 'hy' ? 'Հայ' : 'Eng'}
              </button>
            ))}
          </div>
        </header>

        <Section n="1" title="Примитивы — светлая и тёмная тема">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 'var(--lg-sp-4)', alignItems: 'start' }}>
            <Panel surface="light">{gallery}</Panel>
            <Panel surface="dark">{gallery}</Panel>
          </div>
        </Section>
      </div>
    </div>
  );
}
